/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ExtensibleEnumeration } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';

import { IDobbiesOrderLookupResources, IOrderLookupSearchAdditionalFieldsData } from '../dobbies-order-lookup.props.autogenerated';

/**
 * Render label.
 * @param className - Classname for label.
 * @param value - Text for label.
 * @returns -The JSX Element.
 */
export const labelGroup = (className: string, value: string): JSX.Element => {
    return <div className={className}>{value}</div>;
};

/**
 * Error label.
 * @param responseErrorMessage - Error message for label.
 * @param errorMessageClassName - Error message for class name.
 * @returns - Error ReactNode.
 */
export const renderErrorLabel = (responseErrorMessage: string, errorMessageClassName: string): React.ReactNode => {
    return responseErrorMessage && <div className={errorMessageClassName}>{responseErrorMessage}</div>;
};

/**
 * Registered user message.
 * @param resources - Resource values.
 * @param signInUrl - Signin url.
 * @param signInLinkClassName - Signin link classname.
 * @param registeredUserClassName - Registered user message classname.
 * @returns - ReactNode.
 */
export const renderRegisteredUserMessage = (
    resources: IDobbiesOrderLookupResources,
    signInUrl: string,
    signInLinkClassName: string,
    registeredUserClassName: string
): React.ReactNode => {
    const { orderLookupRegisteredUserText, orderLookupViewAccountText, orderLookupSignInLinkText } = resources;
    const message: string = orderLookupRegisteredUserText;
    const infoMessage: string = orderLookupViewAccountText;
    const signInText: string = orderLookupSignInLinkText;
    return (
        <div className={registeredUserClassName}>
            {message}
            <a className={signInLinkClassName} href={signInUrl}>
                {signInText}
            </a>
            {infoMessage}
        </div>
    );
};

/**
 * EmailValid submit.
 * @param email - Onsubmit of form and button click.
 * @returns - Boolean.
 */
export const isEmailValid = (email: string): boolean => {
    return EmailRegex.defaultRegex.test(email);
};

/**
 * Additional field validation for on submit.
 * @param name - Onsubmit of form and button click.
 * @returns - Boolean.
 */
export const isAdditionalFieldValid = (name: string): boolean => {
    const regEx = new RegExp('^(?!\\s)[A-Za-z0-9\\s]+$', 'u');
    return regEx.test(name);
};

/**
 * Validate order lookup field against extensible enum.
 * @param orderStatusLookUpParameter - Order lookup parameter.
 * @param orderLookupExtensibleEnum - Order lookup extensible enum.
 * @returns - Boolean.
 */
export const validateOrderLookupField = (
    orderStatusLookUpParameter: string,
    orderLookupExtensibleEnum: ExtensibleEnumeration[]
): boolean => {
    let isValidField: boolean = false;
    for (const item of orderLookupExtensibleEnum) {
        if (item.Name.toLocaleLowerCase() === orderStatusLookUpParameter.toLocaleLowerCase()) {
            isValidField = true;
            return isValidField;
        }
    }
    return isValidField;
};

/**
 * Validate order lookup additional fields with proxy values.
 * @param value - Value to be validated.
 * @param orderLookupExtensibleEnum - Order lookup extensible enum.
 * @returns - Boolean.
 */
export const validateAdditionalFieldWithProxy = (value: string, orderLookupExtensibleEnum: ExtensibleEnumeration[]): boolean => {
    return !orderLookupExtensibleEnum.some(item => item.Name.toLocaleLowerCase() === value.toLocaleLowerCase());
};

/**
 * Validate order lookup additional fields against extensible enum.
 * @param orderStatusLookUpAdditionalParameters - Order lookup parameter.
 * @param orderLookupExtensibleEnum - Order lookup extensible enum.
 * @returns - Boolean.
 */
export const validateOrderLookupAdditionalField = (
    orderStatusLookUpAdditionalParameters: IOrderLookupSearchAdditionalFieldsData[],
    orderLookupExtensibleEnum: ExtensibleEnumeration[]
): boolean => {
    let isValidAdditionalField: boolean = false;
    if (ArrayExtensions.hasElements(orderStatusLookUpAdditionalParameters)) {
        orderStatusLookUpAdditionalParameters.map(item => {
            const fieldKey: string = item.fieldKey ?? '';
            if (!isValidAdditionalField) {
                isValidAdditionalField = validateAdditionalFieldWithProxy(fieldKey, orderLookupExtensibleEnum);
            }
            return isValidAdditionalField;
        });
    } else {
        isValidAdditionalField = false;
    }
    return isValidAdditionalField;
};

/**
 * Render validation message if field does not matches with extensible enum.
 * @param orderLookupFieldValidationErrorMessage - Order lookup validation message.
 * @returns - ReactNode.
 */
export const renderFieldErrors = (orderLookupFieldValidationErrorMessage: string): React.ReactNode => {
    return <div className='ms-order-lookup__field-value__error-message'>{orderLookupFieldValidationErrorMessage}</div>;
};
